import axios, { AxiosRequestHeaders, AxiosResponse, CancelTokenSource, RawAxiosRequestHeaders } from 'axios'
import qs from 'qs'
import { BaseError, BaseHook, BaseRequestConfig, BaseToken, BaseUserModule } from '@/types'
import { createHooks } from '@/utils/hook-util'
import { sleep } from '@/utils/timer-util'

const AXIOS_SERVER_BASE_URL = `${process.env.VUE_APP_SERVER_URL}${process.env.VUE_APP_SERVER_CONTEXT_PATH}`
const AXIOS_ACCESS_ID_HEADER_NAME = 'Access-Id'
const AXIOS_AUTHORIZATION_HEADER_NAME = 'Authorization'

const unauthorizedErrorHooks = createHooks<BaseError>()

export default class BaseAxios {
  private requestCanceller: CancelTokenSource
  private waitingRequestConfigs: BaseRequestConfig[]
  private authorization?: string

  constructor() {
    axios.defaults.baseURL = AXIOS_SERVER_BASE_URL
    axios.defaults.headers.common['Content-Type'] = 'application/json'
    axios.defaults.headers.common.Accept = 'application/json'

    this.requestCanceller = axios.CancelToken.source()
    this.waitingRequestConfigs = []

    axios.interceptors.request.use(async (requestConfig: BaseRequestConfig & { headers: AxiosRequestHeaders }) => {
      if (requestConfig.url && requestConfig.urlVariables) {
        Object.entries(requestConfig.urlVariables)
          .forEach((urlVariable) => {
            const [urlVariableName, urlVariableValue] = urlVariable

            requestConfig.url = requestConfig.url?.replace(`:${urlVariableName}`, urlVariableValue)
          })
      }

      const requestConfigHeaders = requestConfig.headers as RawAxiosRequestHeaders

      if (requestConfig.authorized && requestConfigHeaders?.common) {
        const commonRequestConfigHeaders = requestConfigHeaders.common as any

        delete commonRequestConfigHeaders[AXIOS_ACCESS_ID_HEADER_NAME]
        delete commonRequestConfigHeaders[AXIOS_AUTHORIZATION_HEADER_NAME]
      }

      if (requestConfig.params && (!requestConfig.paramsSerializer)) {
        requestConfig.paramsSerializer = {
          serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }

      requestConfig.cancelToken = requestConfig.cancelToken ?? this.requestCanceller.token

      if ((!this.authorization) && (!requestConfig.authorized)) {
        this.waitingRequestConfigs.push(requestConfig)

        while (!this.authorization) await sleep(500)

        const requestConfigIndex = this.waitingRequestConfigs.indexOf(requestConfig)

        if (requestConfigIndex >= 0) this.waitingRequestConfigs.splice(requestConfigIndex, 1)
      }

      return requestConfig
    })
    axios.interceptors.response.use(
      (response) => {
        const requestConfig: BaseRequestConfig = response.request

        if (requestConfig.responseType === 'blob') {
          response.data.filename = (requestConfig as any).responseURL
            ?.split('/').pop()
            .split('?')[0]
            .split('#')[0]
        }

        return response
      },
      (error) => {
        const response: AxiosResponse = error.response

        if (response?.status === 401) unauthorizedErrorHooks.execute(error)

        return Promise.reject(error)
      }
    )
  }

  setUserModule(userModule: BaseUserModule): void {
    const { accessId } = userModule
    const id = (accessId !== '!') ? accessId : undefined

    this.setHeader(AXIOS_ACCESS_ID_HEADER_NAME, id)
  }

  setToken(token: BaseToken): void {
    const { accessToken } = token
    const authorization = accessToken ? `Bearer ${accessToken}` : undefined

    this.setHeader(AXIOS_AUTHORIZATION_HEADER_NAME, authorization)

    this.authorization = authorization
  }

  setLanguage(language: string): void {
    this.setHeader('Accept-Language', language)
  }

  cancelRequests(): void {
    this.requestCanceller.cancel('app.error.requestCanceledError')
    this.requestCanceller = axios.CancelToken.source()
  }

  registerUnauthorizedErrorHook(unauthorizedErrorHook: BaseHook<BaseError>): void {
    unauthorizedErrorHooks.register(unauthorizedErrorHook)
  }

  private setHeader(headerName: string, headerValue?: string): void {
    const requestConfigHeaders = axios.defaults.headers.common

    if (headerValue) {
      requestConfigHeaders[headerName] = headerValue

      this.waitingRequestConfigs
        .map((waitingRequestConfig) => waitingRequestConfig.headers as RawAxiosRequestHeaders)
        .forEach((waitingRequestConfigHeaders) => {
          if (waitingRequestConfigHeaders) waitingRequestConfigHeaders[headerName] = headerValue
        })
    }
    else {
      if (requestConfigHeaders) delete requestConfigHeaders[headerName]

      this.waitingRequestConfigs
        .map((waitingRequestConfig) => waitingRequestConfig.headers as RawAxiosRequestHeaders)
        .forEach((waitingRequestConfigHeaders) => {
          if (waitingRequestConfigHeaders) delete waitingRequestConfigHeaders[headerName]
        })
    }
  }
}

export const baseAxios = new BaseAxios()